/**
 * Submit New Idea / Request Page
 * 
 * Allows users to submit new ideas or feature requests for prioritization.
 * This is the entry point for idea submissions in the workspace.
 * Now backed by Supabase via platform-engine.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { FileText, Plus, CheckCircle, ArrowRight, RefreshCw, Sparkles, Loader2, X, Check, ArrowDownToLine } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { craftIdea, type CraftIdeaResult } from '../../lib/llmIdeaCraftingService';
import { useAiCapability } from '../../lib/ai/useAiCapability';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';
// Direct Supabase integration (optional - falls back to local store)
import { getSupabaseClient } from '../../lib/supabaseClient';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { isModuleEnabled, useWorkspaceModules } from '../../lib/workspaceModulesService';
import { dispatchPrioritizationDashboardRefresh } from '../../lib/prioritizationDashboardRefreshEvents';
import { requireAuthUser } from '../../lib/requireAuthUser';
import { useItemSourcesStore } from '../../stores/prioritization/itemSourcesStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { saveImportRows } from '../../lib/importBatch';
import { useFormDraft } from '../../hooks/useFormDraft';
import { ItemImportExportModal } from '../../components/prioritization/item-list/ItemImportExportModal';
import { SpreadsheetPageDropTarget } from '../../components/prioritization/import-export/SpreadsheetPageDropTarget';
import { IntegrationSyncPanel } from '../../components/prioritization/spaces/IntegrationSyncPanel';

// Do not use slug fallbacks for queue_id — DB expects UUID or null.
// When no org units exist, only "General (No specific queue)" is offered.

export default function SubmitIdea() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDirectEntry = searchParams.get('direct') === 'true';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [selectedQueue, setSelectedQueue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [proposal, setProposal] = useState<CraftIdeaResult | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<{ title: string; itemId: string } | null>(null);
  const [authSessionMissing, setAuthSessionMissing] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [droppedSpreadsheet, setDroppedSpreadsheet] = useState<File | null>(null);
  const ideaCraftingEnabled = useAiCapability('idea_crafting');
  
  const storeUserId = useUserStore((state) => state.userId);
  const storeUserName = useUserStore((state) => state.userName);
  const isImpersonating = useUserStore((state) => state.isImpersonating);
  const addNotification = useUIStore((state) => state.addNotification);
  const items = useItemsStore((state) => state.items);
  const createItemWithReason = useItemsStore((state) => state.createItemWithReason);

  const handleSpreadsheetDrop = useCallback((file: File) => {
    setDroppedSpreadsheet(file);
    setImportModalOpen(true);
  }, []);

  const handleUnsupportedDrop = useCallback((file: File) => {
    addNotification({
      type: 'error',
      title: 'Unsupported file',
      message: `"${file.name}" is not a CSV or Excel file.`,
      duration: 4000,
    });
  }, [addNotification]);
  
  const hasStoreSession = storeUserId && storeUserId !== 'unknown';
  const isSignedIn = !!(hasStoreSession || isImpersonating);
  
  // Get org-based queues (scoped to current workspace so we only show queues for this workspace)
  const orgUnits = useEnterpriseIdentityStore((state) => state.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((state) => state.fetchFromSupabase);
  const workspaceId = getCurrentWorkspaceId();
  const workspaceFeatures = useWorkspaceModules();
  const showOrgHierarchy = isModuleEnabled(workspaceFeatures, 'org_hierarchy');

  const { sources: itemSources, fetchSources } = useItemSourcesStore();
  const activeSources = itemSources.filter((s) => s.isActive && s.kind === 'idea'); // how-it-arrived entries are set by themselves (migration 926)

  useEffect(() => {
    fetchOrgUnits(workspaceId ? { workspaceId } : undefined);
  }, [fetchOrgUnits, workspaceId]);

  useEffect(() => {
    if (workspaceId) fetchSources(workspaceId);
  }, [workspaceId, fetchSources]);
  
  const availableQueues = useMemo(() => {
    return Array.from(orgUnits.values())
      .filter(ou => {
        if (!ou.queueSettings?.enabled) return false;
        // Only show queues for current workspace or unscoped (null) org units
        if (workspaceId == null) return true;
        return ou.workspaceId === workspaceId || ou.workspaceId == null;
      })
      .map(ou => ({
        id: ou.id,
        label: ou.displayName,
        description: ou.queueSettings?.description || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUnits, workspaceId]);
  
  // DB-first draft autosave: in-progress input is persisted to Supabase (the
  // `form_drafts` table), so a page refresh restores the form instead of losing it.
  // Truth stays in the DB — nothing here is kept in localStorage or Zustand.
  const draft = useFormDraft<{ title: string; description: string; source: string; selectedQueue: string }>({
    draftKey: isDirectEntry ? 'submit-idea:direct' : 'submit-idea',
    value: { title, description, source, selectedQueue },
    enabled: isSignedIn,
    isEmpty: (v) => !v.title.trim() && !v.description.trim() && !v.source.trim() && !v.selectedQueue.trim(),
    onHydrate: (v) => {
      setTitle(v.title ?? '');
      setDescription(v.description ?? '');
      setSource(v.source ?? '');
      setSelectedQueue(v.selectedQueue ?? '');
    },
  });

  const handleImproveWithAI = async () => {
    const hasContent = title.trim() || description.trim();
    if (!hasContent) {
      addNotification({
        type: 'error',
        title: 'Add some content first',
        message: 'Enter a title or description, then click Improve with AI',
        duration: 3000,
      });
      return;
    }
    setProposal(null);
    setImproving(true);
    try {
      const result = await craftIdea({ title: title.trim(), description: description.trim() });
      setProposal(result);
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Improvement failed',
        message: err instanceof Error ? err.message : 'Could not get suggestions',
        duration: 5000,
      });
    } finally {
      setImproving(false);
    }
  };

  const handleAcceptProposal = () => {
    if (proposal) {
      setTitle(proposal.suggestedTitle);
      setDescription(proposal.suggestedDescription);
      setProposal(null);
      addNotification({
        type: 'success',
        title: 'Proposal applied',
        message: 'You can edit further before submitting.',
        duration: 3000,
      });
    }
  };

  const handleRejectProposal = () => {
    setProposal(null);
  };

  // Prevent any automatic redirects - component should render normally
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      addNotification({
        type: 'error',
        title: 'Title Required',
        message: 'Please enter a title for your idea or request',
        duration: 3000
      });
      return;
    }
    
    if (!description.trim()) {
      addNotification({
        type: 'error',
        title: 'Description Required',
        message: 'Please provide a description of your idea',
        duration: 3000
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Store the title before async operations
    const submittedTitle = title.trim();
    
    // No local storage fallback - all submissions go to database
    
    // All submissions go to Supabase - no local fallback
    
    // Require authentication (Clerk or Supabase /auth/login or impersonation)
    if (!isSignedIn) {
      addNotification({
        type: 'error',
        title: 'Authentication Required',
        message: 'Please sign in to submit ideas.',
        duration: 5000
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Require workspace so we don't insert with null and so user gets clear feedback
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        addNotification({
          type: 'error',
          title: 'Select a workspace',
          message: 'Please select a workspace (e.g. Greenfield Co.) in the sidebar, then try submitting again.',
          duration: 8000,
        });
        setIsSubmitting(false);
        return;
      }

      const supabase = getSupabaseClient();
      const { user: authUser } = await requireAuthUser(supabase);
      if (!authUser) {
        console.log('[AUTH] missing on page', { page: 'SubmitIdea', location: window.location.href });
        setAuthSessionMissing(true);
        setIsSubmitting(false);
        return;
      }
      setAuthSessionMissing(false);

      // queue_id must be UUID or null — never label/slug (e.g. "product")
      const queueId = selectedQueue === '' ? null : selectedQueue;
      const queueLabel = queueId ? availableQueues.find(q => q.id === queueId)?.label : null;

      // All submitted ideas start as active items.
      // Direct entry bypasses the standard queue but is still active.
      const source_value = source.trim() || (isDirectEntry ? 'Direct entry' : 'Idea submission');
      
      console.log('[SubmitIdea] Creating item:', {
        isDirectEntry,
        source: source_value,
        url: window.location.href,
      });
      
      // Save through the items store, not a direct insert: the store is where
      // the policy check, queue/space stamping and auto-classification live, so
      // an idea submitted here is classified like an item created anywhere else.
      const created = await createItemWithReason({
        title: submittedTitle,
        description: description.trim(),
        createdBy: authUser.id,
        created_by_user_id: authUser.id,
        source: source_value,
        // Marks this row as a hand-in (not an import), whatever source label
        // was picked — the server tells workspace admins it is waiting (751).
        submittedVia: isDirectEntry ? 'direct_entry' : 'idea_form',
        tags: queueLabel ? [queueLabel] : [],
        // null = "no queue chosen": keep the item out of the root queue, as before.
        queueId,
      });

      if (!created.ok || !created.id) {
        throw new Error(created.reason ?? 'The idea could not be saved.');
      }

      const candidateId = created.id;

      dispatchPrioritizationDashboardRefresh({ source: 'submit-idea' });

      addNotification({
        type: 'success',
        title: 'Idea Submitted',
        message: `"${submittedTitle}" has been submitted${queueLabel ? ` to ${queueLabel}` : ''} successfully`,
        duration: 5000
      });
      
      // Reset form
      setTitle('');
      setDescription('');
      setSource('');
      setSelectedQueue('');

      // The idea is now a real `items` row — drop the in-progress draft so a later
      // visit (or refresh) starts clean instead of re-hydrating committed input.
      void draft.discard();

      // Show success state with the new item id for direct navigation.
      setSubmissionSuccess({ title: submittedTitle, itemId: candidateId });
    } catch (error) {
      console.error('Supabase submission failed:', error);
      
      const err = error instanceof Error ? error : new Error(String(error));
      const errorMessage = err.message;
      const isAbort = err.name === 'AbortError';
      const isConfigError = errorMessage.includes('environment variable') || 
                           errorMessage.includes('VITE_SUPABASE');
      
      if (isConfigError) {
        addNotification({
          type: 'error',
          title: 'Configuration Error',
          message: 'Database is not configured. Please contact your administrator.',
          duration: 8000
        });
      } else if (isAbort) {
        addNotification({
          type: 'error',
          title: 'Submission timed out or was cancelled',
          message: 'The request was aborted. Please check your connection, ensure a workspace is selected, and try again.',
          duration: 8000
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Submission Failed',
          message: `Failed to submit idea: ${errorMessage}. Please try again.`,
          duration: 8000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Permission check: use store's can() which checks capabilities first, admin role fallback
  const canSubmit = useUserStore((state) => state.can('items.create'));
  
  if (!canSubmit) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--mustard-dim)] rounded-full">
              <FileText className="h-6 w-6 text-[var(--mustard)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--mustard)] mb-2">Access Restricted</h2>
              <p className="text-sm text-[var(--mustard)] mb-2">
                You don't have permission to submit new ideas. This action requires the <strong>items.create</strong> permission.
              </p>
              <p className="text-xs text-[var(--mustard)]">
                Roles with this permission: Workspace Admin, Tenant Admin, Platform Admin (or users with items.create capability)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <SpreadsheetPageDropTarget
        enabled={!importModalOpen}
        onFile={handleSpreadsheetDrop}
        onUnsupportedFile={handleUnsupportedDrop}
        label="Drop to add items to intake"
      />
      {/* Header */}
      <PageHeader
        icon={<FileText className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title={isDirectEntry ? 'New item' : 'Submit an idea'}
        subtitle={isDirectEntry
          ? 'Create a new item directly (bypasses triage queue)'
          : 'Submit a new idea, feature request, or work item for prioritization'
        }
      />
      
      {authSessionMissing && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)] mb-2">Please sign in to submit ideas.</p>
          <Link to="/auth/login" className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] text-sm font-medium">
            Sign In
          </Link>
        </div>
      )}

      {/* Miro / Jira bar — geography-aware Miro import (zones, color maps, hierarchy) */}
      <IntegrationSyncPanel
        listId="intake"
        listName="Idea Intake"
        priorities={[]}
        hideExports
        onImportItems={(imported) =>
          // Await each create and count only rows the store confirms saved —
          // counting attempts announced a Miro import as a success while every
          // insert was being refused. The panel toasts from this outcome.
          saveImportRows(imported, (item) => {
            if (!item.title) return Promise.resolve(null);
            const altitude =
              item.altitudeLevel !== undefined && item.altitudeLevel !== null
                ? String(item.altitudeLevel)
                : undefined;
            return createItemWithReason({
              title: item.title,
              description: typeof item.description === 'string' ? item.description : undefined,
              source: (typeof item.source === 'string' && item.source) || 'Import',
              createdBy: storeUserName || 'Unknown',
              tags: Array.isArray(item.tags)
                ? (item.tags as string[])
                : ['imported'],
              ...(typeof item.itemCategory === 'string' ? { itemCategory: item.itemCategory as never } : {}),
              ...(altitude ? { altitudeLevel: altitude as never } : {}),
              ...(typeof item.metaCategory === 'string' ? { metaCategory: item.metaCategory as never } : {}),
              ...(typeof item.strategicBucket === 'string'
                ? { strategicBucket: item.strategicBucket as never }
                : {}),
            });
          }, (item) => item.title || 'Untitled')
        }
        onNotification={(n) => addNotification({ ...n, duration: n.duration ?? 4000 })}
        onCsvImportClick={() => {
          setDroppedSpreadsheet(null);
          setImportModalOpen(true);
        }}
        onCsvTemplateClick={() => {
          setDroppedSpreadsheet(null);
          setImportModalOpen(true);
        }}
      />

      {/* Multi-source import chooser (whiteboard, JSON, etc.) */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-heading font-semibold text-[var(--text-pri)]">Add items to intake</h2>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              Import from a spreadsheet, whiteboard photo, JSON, or another available source. You can also drop CSV or Excel anywhere on this page. Use the Miro / Jira bar above for geography-aware Miro import.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setDroppedSpreadsheet(null);
              setImportModalOpen(true);
            }}
            className="btn btn-primary inline-flex flex-shrink-0 items-center justify-center gap-2"
          >
            <ArrowDownToLine className="h-4 w-4" />
            Select an import tool
          </button>
        </div>
      </div>

      {/* Bulk Import entry point */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-[var(--hover-tint)]" />
        <span className="text-xs text-[var(--text-ter)] whitespace-nowrap">or submit one at a time below</span>
        <div className="flex-1 h-px bg-[var(--hover-tint)]" />
      </div>

      {/* Submission Form */}
      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">
            Idea Submission Form
          </h2>
          {isSignedIn && (title.trim() || description.trim()) && (
            <span className="text-xs text-[var(--text-ter)]" aria-live="polite">
              {draft.status === 'saving'
                ? 'Saving draft…'
                : draft.lastSavedAt
                  ? 'Draft saved — safe to refresh'
                  : draft.hydratedFromDraft
                    ? 'Draft restored'
                    : ''}
            </span>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-[var(--text-ter)]">
              Write your idea below. Use &quot;Improve with AI&quot; to get a clearer title and description.
            </p>
            {ideaCraftingEnabled && (
            <button
              type="button"
              onClick={handleImproveWithAI}
              disabled={improving || (!title.trim() && !description.trim())}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {improving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {improving ? 'Improving…' : 'Improve with AI'}
            </button>
            )}
          </div>
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Title <span className="text-[var(--coral)]">*</span>
              <span className="text-[var(--text-ter)] font-normal ml-1">(What is wanted?)</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Automated report generation for weekly metrics"
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Description <span className="text-[var(--coral)]">*</span>
              <span className="text-[var(--text-ter)] font-normal ml-1">(What does it get you?)</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Saves 4 hours per week of manual work and reduces reporting errors by providing consistent, automated outputs"
              rows={6}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          {/* Queue Selection */}
          {showOrgHierarchy && (
          <div>
            <label htmlFor="queue" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Route to Queue
              <span className="text-[var(--text-ter)] font-normal ml-1">(Optional)</span>
            </label>
            <select
              id="queue"
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-[var(--bg-raised)]"
            >
              <option value="">General (No specific queue)</option>
              {availableQueues.map((queue) => (
                <option key={queue.id} value={queue.id}>{queue.label}</option>
              ))}
            </select>
            {selectedQueue && (
              <p className="text-xs text-[var(--text-ter)] mt-1">
                {availableQueues.find((queue) => queue.id === selectedQueue)?.description || 'This queue is enabled for direct submissions.'}
              </p>
            )}
            {orgUnits.size > 0 && availableQueues.length === 0 && (
              <p className="text-xs text-[var(--mustard)] mt-1">
                No queues are selectable. Go to <Link to="/prioritization/settings/queues" className="font-semibold underline">Settings → Queues</Link>, open the org node that should receive items, turn on <strong>Enable as Queue</strong>, then save.
              </p>
            )}
            {orgUnits.size === 0 && (
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Select a workspace in the sidebar to see its queues, then configure queue-enabled org nodes in <Link to="/prioritization/settings/queues" className="font-semibold underline">Settings → Queues</Link>.
              </p>
            )}
            {orgUnits.size > 0 && availableQueues.length > 0 && (
              <p className="text-xs text-[var(--text-ter)] mt-1">
                If you know which team should handle this, select their queue.
              </p>
            )}
          </div>
          )}
          
          <div>
            <label htmlFor="source" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Source
              <span className="text-[var(--text-ter)] font-normal ml-1">(Optional)</span>
            </label>
            <select
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-[var(--bg-raised)]"
            >
              <option value="">Select a source...</option>
              {activeSources.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                // Use relative path to go up one level to workspace
                navigate('../workspace', { replace: true });
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Plus className="h-4 w-4" />
              {isSubmitting ? 'Submitting...' : isDirectEntry ? 'New item' : 'Submit an idea'}
            </button>
          </div>
        </form>
      </div>

      {/* AI proposal modal – accept or reject, never overwrite without choice */}
      {proposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-[var(--slate-dim)]">
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">AI suggestion</h3>
              <button
                type="button"
                onClick={handleRejectProposal}
                className="p-1.5 text-[var(--text-sec)] hover:text-[var(--text-sec)] rounded"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <p className="text-sm text-[var(--text-sec)]">
                Review the suggestion below. Accept to copy into your form (you can still edit), or reject to keep your current text.
              </p>
              <div>
                <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Suggested title</label>
                <div className="px-3 py-2 bg-[var(--surface)] border border-[var(--rule)] text-[var(--text-pri)]">
                  {proposal.suggestedTitle || '(none)'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Suggested description</label>
                <div className="px-3 py-2 bg-[var(--surface)] border border-[var(--rule)] text-[var(--text-pri)] whitespace-pre-wrap min-h-[4rem]">
                  {proposal.suggestedDescription || '(none)'}
                </div>
              </div>
              {proposal.reasoning && (
                <p className="text-sm text-[var(--text-ter)] italic">{proposal.reasoning}</p>
              )}
              {proposal.tips && proposal.tips.length > 0 && (
                <ul className="text-sm text-[var(--text-sec)] list-disc list-inside space-y-0.5">
                  {proposal.tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-3 px-4 py-3 border-t bg-[var(--surface)]">
              <button
                type="button"
                onClick={handleRejectProposal}
                className="px-4 py-2 text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={handleAcceptProposal}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Success State */}
      {submissionSuccess && (
        <div className="card bg-[var(--green-dim)] border-[var(--green)]">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-[var(--green-dim)] rounded-full">
              <CheckCircle className="h-6 w-6 text-[var(--green)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-1">
                {isDirectEntry ? 'Item Created Successfully!' : 'Idea Submitted Successfully!'}
              </h3>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mb-4">
                &quot;{submissionSuccess.title}&quot; has been added successfully and is ready for continued item work.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/prioritization/items/${submissionSuccess.itemId}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green)] text-white font-medium hover:bg-[var(--green)] transition-colors text-sm"
                >
                  View Item Detail
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/prioritization/intake/mine"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] font-medium hover:bg-[var(--green-dim)] transition-colors text-sm"
                >
                  View My Submissions
                </Link>
                <button
                  onClick={() => setSubmissionSuccess(null)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] font-medium text-sm"
                >
                  <RefreshCw className="h-4 w-4" />
                  Submit Another
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importModalOpen && (
        <ItemImportExportModal
          mode="import"
          items={items}
          initialFile={droppedSpreadsheet}
          onClose={() => {
            setImportModalOpen(false);
            setDroppedSpreadsheet(null);
          }}
        />
      )}

    </div>
  );
}

